import { AnchorHTMLAttributes, cloneElement, Fragment, memo, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { NavLink, NavLinkProps } from "react-router-dom";
import { Badge } from "../../Badge";
import { Tooltip } from "../../Tooltip";
import { useAppNavigationControlContext, useAppNavigationStateContext } from "../AppNavigation";
import { AnchorMenuItemProps, ButtonMenuItemProps, MenuItemDescription, NavLinkMenuItemProps } from "../types";

import { ReactComponent as ChevronIcon } from "./icons/chevron.svg";

import styles from "./styles.module.scss";

type RestPropsType =
  Omit<NavLinkMenuItemProps, "componentProps"> &
  Omit<AnchorMenuItemProps, "componentProps"> &
  Omit<ButtonMenuItemProps, "componentProps">

export type NavItemProps = MenuItemDescription & {
  className?: string,
  level?: number,
  getPortalNode?: (level: number) => Element | DocumentFragment | null,
  defaultIsOpen?: boolean
};

export const NavItem = memo<NavItemProps>((
  {
    id,
    items,
    label,
    category,
    icon,
    componentProps,
    badgeProps,
    level = 1,
    getPortalNode,
    defaultIsOpen,
    className,
    ...restProps
  }) => {
  const didMount = useRef(false);
  const [ isOpen, setIsOpen ] = useState(defaultIsOpen);
  const { type, menuItemMinimized, showTooltip, tooltipPlacement, isMinimized, isExpanded } = useAppNavigationStateContext();
  const { setMenuIsOpen } = useAppNavigationControlContext();

  useEffect(() => {
    if (didMount.current) {
      if (level !== 0) {
        setIsOpen(false);
      }
    } else {
      didMount.current = true;
    }
  }, [ level, type ]);

  useEffect(() => {
    if (didMount.current) {
      if (level !== 0) {
        if (isMinimized) {
          setIsOpen(false);
        }
        if (type === "sidebar" && !isExpanded) {
          setIsOpen(false);
        }
      }
    } else {
      didMount.current = true;
    }
  }, [ isMinimized, isExpanded, type, level ]);


  const { path, externalPath, onClick } = restProps as RestPropsType;

  const groupedItems = useMemo(() => {
    return items?.reduce((acc, v) => {
      let category = acc.find(x => x.group === v.category);
      if (!category) {
        category = {
          group: v.category,
          items: [],
        };
        acc.push(category);
      }
      category.items.push(v);
      return acc;
    }, [] as { group: string | undefined, items: MenuItemDescription[] }[]);
  }, [ items ]);

  let component: ReactNode;
  if (path) {
    component = (
      <NavLink
        key="component"
        to={path}
        {...componentProps as Pick<NavLinkMenuItemProps, "componentProps">}
        onClick={event => {
          (componentProps as NavLinkProps)?.onClick?.(event);
          if (!event.isPropagationStopped() && !event.isDefaultPrevented()) {
            setMenuIsOpen(false);
          }
        }}
      />
    );
  } else if (externalPath) {
    component = (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a
        key="component"
        href={externalPath}
        {...componentProps as Pick<AnchorMenuItemProps, "componentProps">}
        onClick={event => {
          (componentProps as AnchorHTMLAttributes<HTMLAnchorElement>)?.onClick?.(event);
          if (!event.isPropagationStopped() && !event.isDefaultPrevented()) {
            setMenuIsOpen(false);
          }
        }}
      />
    );
  } else {
    component = (
      <button
        key="component"
        onClick={items?.length
          ? (() => {setIsOpen(prevState => !prevState);})
          : event => {
            onClick?.(event);
            if (!event.isPropagationStopped() && !event.isDefaultPrevented()) {
              setMenuIsOpen(false);
            }
          }
        }
        {...componentProps as Pick<ButtonMenuItemProps, "componentProps">}
      />
    );
  }

  return (
    <>
      <Tooltip key="tooltip" content={showTooltip && label} placement={tooltipPlacement}>
        {
          cloneElement(
            component,
            {
              className: classNames(
                styles.root,
                {
                  [styles.open]: isOpen,
                  [styles.hasItems]: !!items?.length,
                },
                className,
              ),
              "data-menu-item-open": items?.length ? isOpen : undefined,
              "data-menu-item-type": items?.length ? "root" : "leaf",
              "data-menu-item-level": level,
              "data-menu-item-has-icon": !!icon,
              "data-menu-item-minimized": menuItemMinimized,
            },
            <div
              key={"content"}
              className={styles.content}
              data-menu-item-content={true}
            >
              {icon &&
                <div
                  key="icon"
                  className={styles.icon}
                  data-menu-item-icon="true"
                >
                  {icon}
                </div>
              }
              {(!menuItemMinimized && label) &&
                <div
                  key="label"
                  className={styles.label}
                  data-menu-item-label="true"
                >
                  {label}
                </div>
              }
            </div>,
            !!badgeProps && (
              <Badge
                key="badge"
                {...badgeProps}
                badgeSize="sm"
                badgeType="pillColor"
                badgeColor="gray"
                data-menu-item-badge="true"
              />
            ),
            !!items?.length && (
              <ChevronIcon
                key="chevron"
                className={styles.chevron}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsOpen(prevState => !prevState);
                }}
                data-menu-item-chevron="true"
              />
            ),
          )
        }
      </Tooltip>
      {
        isOpen
          ? (() => {
            const childItems = groupedItems?.map(category =>
              <Fragment key={category.group ?? "default"}>
                {category.group &&
                  <div
                    key="group"
                    className={styles.category}
                    data-menu-item-category="true"
                    data-menu-item-level={level + 1}
                  >
                    {category.group}
                  </div>
                }
                {category.items.map(value => (
                  <NavItem
                    key={value.id}
                    level={level + 1}
                    getPortalNode={getPortalNode}
                    {...value}
                  />
                ))}
              </Fragment>,
            );
            const portalNode = getPortalNode?.(level + 1);
            return portalNode ? createPortal(childItems, portalNode) : childItems;
          })()
          : null
      }
    </>
  );
});